<div class="h-screen flex flex-col overflow-hidden bg-pageBg">
    <app-header class="shrink-0"></app-header>
    <div class="flex-1 flex flex-col min-h-0 overflow-hidden pt-4 pb-[44px] text-gray-800 antialiased">
        <div class="w-full mx-auto px-4 xl:px-8 flex flex-col flex-1 h-full overflow-y-auto min-h-0 pb-12">
            <!-- Header Section -->
            <div style="background-color: aliceblue;"
                class="relative px-6 py-10 mb-6 bg-white border border-gray-200 shadow-sm rounded-xl overflow-hidden flex flex-col sm:flex-row items-center justify-between">
                <div
                    class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-brandPurple via-brandPurpleDark to-brandAccent">
                </div>
                <div>
                    <h1 class="text-xl f-700 text-brandPurple tracking-tight">Settings</h1>
                    <p class="text-gray-500 mt-1">Configure global security policies and system configurations.</p>
                </div>

                <div class="flex items-center gap-4 mt-4 sm:mt-0">
                    <button
                        class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-1.5 rounded-md text-sm font-semibold transition"
                        (click)="getPasswordPolicy(false)">
                        Refresh Policy
                    </button>
                </div>
            </div>

            <!-- Success / Error Messages -->
            <div *ngIf="message"
                class="mb-4 bg-green-50 border-l-4 border-green-500 p-4 rounded-md shadow-sm flex justify-between items-center">
                <p class="text-green-700 text-sm font-bold">{{ message }}</p>
                <button (click)="message = null"
                    class="text-green-500 hover:text-green-700 text-xl font-bold">&times;</button>
            </div>
            <div *ngIf="errorMessage"
                class="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-md shadow-sm flex justify-between items-center">
                <p class="text-red-700 text-sm font-bold">{{ errorMessage }}</p>
                <button (click)="errorMessage = null"
                    class="text-red-500 hover:text-red-700 text-xl font-bold">&times;</button>
            </div>

            <!-- Main Content -->
            <div class="grid grid-cols-1 md:grid-cols-1 gap-6">

                <!-- Loading State -->
                <div *ngIf="!isLoaded"
                    class="flex justify-center items-center py-20 bg-white border border-gray-200 rounded-xl shadow-sm">
                    <div class="flex flex-col items-center gap-4">
                        <!-- Modern CSS Spinner -->
                        <div class="w-10 h-10 border-4 border-gray-200 border-t-brandPurple rounded-full animate-spin">
                        </div>
                        <p class="text-gray-400 text-sm font-medium">Fetching security policies...</p>
                    </div>
                </div>

                <!-- Password Policy Card -->
                <div *ngIf="isLoaded" class="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <div style="background-color: aliceblue;"
                        class="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                        <h2 class="text-lg f-700 text-brandPurple">Password Policy</h2>
                    </div>

                    <div class="p-6">
                        <!-- Info Box -->
                        <div
                            class="mb-6 bg-blue-50 border-l-4 border-brandPurpleDark p-4 rounded-md flex items-start gap-3">
                            <i class="zmdi zmdi-info-outline text-brandPurpleDark text-lg mt-0.5"></i>
                            <div>
                                <p class="text-gray-700 text-sm font-medium">Modify your existing password policy below.
                                </p>
                                <p class="text-gray-500 text-xs mt-1">A password policy is a set of rules that define
                                    the
                                    type of password a user can set.</p>
                            </div>
                        </div>

                        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <!-- Rule Logic -->
                            <div class="space-y-4">
                                <h3
                                    class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">
                                    Complexity Rules</h3>

                                <label
                                    class="flex items-center gap-3 p-3 hover:bg-pageBg rounded-lg cursor-pointer transition border border-transparent hover:border-gray-100">
                                    <input type="checkbox"
                                        class="w-5 h-5 rounded border-gray-300 text-brandAccent focus:ring-brandAccent"
                                        [checked]="passwordTerms.contain_upper_case === 1"
                                        (change)="passwordTerms.contain_upper_case = $any($event.target).checked ? 1 : 0">
                                    <div class="flex flex-col">
                                        <span class="text-sm font-semibold text-gray-700">Uppercase Requirement</span>
                                        <span class="text-xs text-gray-400">Require at least one uppercase letter
                                            (A-Z)</span>
                                    </div>
                                </label>

                                <label
                                    class="flex items-center gap-3 p-3 hover:bg-pageBg rounded-lg cursor-pointer transition border border-transparent hover:border-gray-100">
                                    <input type="checkbox"
                                        class="w-5 h-5 rounded border-gray-300 text-brandAccent focus:ring-brandAccent"
                                        [checked]="passwordTerms.contain_lower_case === 1"
                                        (change)="passwordTerms.contain_lower_case = $any($event.target).checked ? 1 : 0">
                                    <div class="flex flex-col">
                                        <span class="text-sm font-semibold text-gray-700">Lowercase Requirement</span>
                                        <span class="text-xs text-gray-400">Require at least one lowercase letter
                                            (a-z)</span>
                                    </div>
                                </label>

                                <label
                                    class="flex items-center gap-3 p-3 hover:bg-pageBg rounded-lg cursor-pointer transition border border-transparent hover:border-gray-100">
                                    <input type="checkbox"
                                        class="w-5 h-5 rounded border-gray-300 text-brandAccent focus:ring-brandAccent"
                                        [checked]="passwordTerms.contain_number === 1"
                                        (change)="passwordTerms.contain_number = $any($event.target).checked ? 1 : 0">
                                    <div class="flex flex-col">
                                        <span class="text-sm font-semibold text-gray-700">Numeric Requirement</span>
                                        <span class="text-xs text-gray-400">Require at least one number (0-9)</span>
                                    </div>
                                </label>

                                <label
                                    class="flex items-center gap-3 p-3 hover:bg-pageBg rounded-lg cursor-pointer transition border border-transparent hover:border-gray-100">
                                    <input type="checkbox"
                                        class="w-5 h-5 rounded border-gray-300 text-brandAccent focus:ring-brandAccent"
                                        [checked]="passwordTerms.contain_special_character === 1"
                                        (change)="passwordTerms.contain_special_character = $any($event.target).checked ? 1 : 0">
                                    <div class="flex flex-col">
                                        <span class="text-sm font-semibold text-gray-700">Special Character
                                            Requirement</span>
                                        <span class="text-xs text-gray-400">Require at least one non-alphanumeric
                                            character
                                            (!@#$...)</span>
                                    </div>
                                </label>
                            </div>

                            <!-- Bounds Logic -->
                            <div class="space-y-6">
                                <h3
                                    class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 border-b border-gray-100 pb-2">
                                    Policy Constraints</h3>

                                <div class="p-4 bg-gray-50 rounded-xl border border-gray-200">
                                    <label class="block text-sm font-semibold text-gray-700 mb-2">Minimum Password
                                        Length</label>
                                    <div class="relative">
                                        <input type="number"
                                            class="w-full form-input p-3 border border-gray-300 rounded-lg text-sm focus:ring-brandAccent focus:border-brandAccent transition shadow-sm"
                                            [(ngModel)]="passwordTerms.minimum_length" placeholder="Length (e.g. 8)">
                                        <div class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                                            <i class="zmdi zmdi-key"></i>
                                        </div>
                                    </div>
                                    <p class="text-red-500 text-[11px] font-bold mt-2 flex items-center gap-1"
                                        *ngIf="passwordTerms.minimum_length && passwordTerms.minimum_length < 5">
                                        <i class="zmdi zmdi-alert-circle"></i> Minimum password length should be 5.
                                    </p>
                                </div>

                                <!-- Actions -->
                                <div class="pt-4 flex flex-col sm:flex-row gap-3">
                                    <button
                                        class="flex-1 bg-brandAccent hover:bg-brandPurple text-white py-3 rounded-lg text-sm font-bold shadow-md transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                                        (click)="updatePasswordPolicy(passwordTerms)"
                                        [disabled]="!passwordTerms.minimum_length || passwordTerms.minimum_length < 5">
                                        Update Policy
                                    </button>
                                    <button
                                        class="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg text-sm font-bold transition active:scale-95"
                                        (click)="resetPasswordPolicy()">
                                        Reset to Default
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Footer Info -->
                    <div
                        class="px-6 py-4 bg-gray-50 border-t border-gray-200 text-[11px] text-gray-400 font-medium italic">
                        Note: Updates are reflected immediately for all new password changes across the system.
                    </div>
                </div>
            </div>
        </div>
        <footer class="login-footer">
            <span *ngIf="copyrightDetails">
                &copy; {{ copyrightDetails.from_year }} - {{ copyrightDetails.to_year }},
                Retail Calculus or its affiliates. All rights reserved.
            </span>
            <span *ngIf="!copyrightDetails">
                &copy; 2016 - 2026, Retail Calculus or its affiliates. All rights reserved.
            </span>

            <div class="footer-links">
                <a href="javascript:void(0)">Privacy Policy</a>
                <a href="javascript:void(0)">Terms of Use</a>
                <span class="version" *ngIf="copyrightDetails?.app_version">
                    Version {{ copyrightDetails.app_version }}
                </span>
            </div>
        </footer>
    </div>
</div>